import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { queryKeys, invalidateQueries } from '../lib/queryKeys';
import { getApiErrorMessage } from '../lib/apiErrors';

// Email from archived customers in an organization, waiting for an admin (Org Settings → Held Emails)
export function useHeldEmails(organizationId?: string) {
  return useQuery({
    queryKey: queryKeys.heldEmails.list(organizationId),
    queryFn: async () => {
      const response = await apiService.getHeldEmails(organizationId);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to load held emails'));
      }
      return response.data ?? [];
    },
    enabled: !!organizationId,
  });
}

// Restore the archived customer who sent a held email and process it (admins of the email's organization)
export function useReleaseHeldEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.releaseHeldEmail(id);
      if (!response.success || !response.data) {
        throw new Error(getApiErrorMessage(response, 'Failed to release the email'));
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.heldEmails.all });
      // The customer is active again and the email may have opened or updated a ticket
      queryClient.invalidateQueries({ queryKey: invalidateQueries.customers.all() });
      queryClient.invalidateQueries({ queryKey: invalidateQueries.tickets.all() });
    },
  });
}

// Permanently delete a held email (admins of the email's organization)
export function useDiscardHeldEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiService.discardHeldEmail(id);
      if (!response.success) {
        throw new Error(getApiErrorMessage(response, 'Failed to discard the email'));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.heldEmails.all });
    },
  });
}
